import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Tabs,
  Tab,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import WorkRequestHandler from '../components/work-requests/WorkRequestHandler';
import InterventionCalendar from '../components/work-requests/InterventionCalendar';
import { db } from '../firebase/config';
import { collection, query, where, orderBy, getDocs, doc, getDoc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { COLLECTIONS } from '../firebase/collections';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

const STATUS_DISPLAY = {
  pending: 'En attente', in_progress: 'En cours', completed: 'Terminé', cancelled: 'Annulé', pending_validation: 'En attente de validation'
};
const PRIORITY_DISPLAY = { low: 'Basse', medium: 'Moyenne', high: 'Haute', urgent: 'Urgente' };
const CATEGORY_DISPLAY = {
  plumbing: 'Plomberie', electrical: 'Électricité', electricity: 'Électricité',
  carpentry: 'Menuiserie', painting: 'Peinture', hvac: 'Climatisation',
  it: 'Informatique', cleaning: 'Nettoyage', gardening: 'Jardinage',
  locksmith: 'Serrurerie', safety: 'Sécurité / Urgence', other: 'Autre'
};
const translateCategory = (v) => v ? (CATEGORY_DISPLAY[v.toLowerCase?.()] || v) : 'Non spécifié';
const extractLocation = (loc) => {
  if (!loc) return 'Non spécifié';
  if (typeof loc === 'string') return loc;
  const parts = [loc.floor, loc.room].filter(Boolean);
  return parts.length > 0 ? parts.join(' — ') : (loc.building || 'Non spécifié');
};

// Fonction utilitaire pour calculer le temps de résolution moyen
const calculateAverageResolutionTime = (requests) => {
  const completedRequests = requests.filter(req => req.status === 'completed' && req.completedAt && req.createdAt);
  
  if (completedRequests.length === 0) return 0;
  
  const totalTime = completedRequests.reduce((sum, req) => {
    const createdDate = req.createdAt instanceof Date ? req.createdAt : req.createdAt.toDate();
    const completedDate = req.completedAt instanceof Date ? req.completedAt : req.completedAt.toDate();
    return sum + (completedDate - createdDate);
  }, 0);
  
  // Retourne le temps moyen en heures
  return Math.round(totalTime / completedRequests.length / (1000 * 60 * 60));
};

const TechnicalServiceDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [stats, setStats] = useState({
    byStatus: {},
    byPriority: {},
    recentActivity: [],
    averageResolutionTime: 0,
    weeklyTrends: []
  });
  const { currentUser, userService } = useAuth();

  // Planification
  const [planningRequest, setPlanningRequest]   = useState(null);
  const [planningForm, setPlanningForm]         = useState({ date: '', time: '09:00', duration: 60, technicianName: '', equipment: '' });
  const [planningLoading, setPlanningLoading]   = useState(false);
  const [planningError, setPlanningError]       = useState('');
  const [interventionsMap, setInterventionsMap] = useState({});

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Si userService n'est pas défini, on vérifie d'abord dans la base de données
      let serviceId = userService?.id;
      if (!serviceId && currentUser) {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, currentUser.uid));
        if (userDoc.exists() && userDoc.data().serviceId) {
          serviceId = userDoc.data().serviceId;
        }
      }

      if (!serviceId) {
        setError('Service technique non trouvé. Veuillez contacter votre administrateur.');
        setLoading(false);
        return;
      }
      
      // Récupérer les demandes assignées à ce service technique
      const q = query(
        collection(db, COLLECTIONS.WORK_REQUESTS),
        where('assignedServiceId', '==', serviceId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const requestsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate() || null,
          // Utiliser directement le champ createdByName de Firestore pour le nom du demandeur
          requestingServiceName: data.requestingServiceName ||
                                data.createdByName ||
                                data.serviceName ||
                                data.createdByEmail ||
                                ((data.isAnonymous || data.sectorName) ? `QR — ${data.sectorName || 'Secteur'}` : 'Demandeur'),
          // Utiliser le champ category de Firestore pour le type
          problemType: data.category || 
                      data.problemType || 
                      data.problem || 
                      'Non spécifié',
          serviceType: data.category || 
                      data.problemType || 
                      data.problem || 
                      'Non spécifié'
        };
      });

      // Calculer les statistiques
      const statusStats = {};
      const priorityStats = {};

      requestsData.forEach(request => {
        const statusLabel = STATUS_DISPLAY[request.status] || request.status || 'Non défini';
        statusStats[statusLabel] = (statusStats[statusLabel] || 0) + 1;

        const priorityLabel = PRIORITY_DISPLAY[request.priority] || request.priority || 'Non défini';
        priorityStats[priorityLabel] = (priorityStats[priorityLabel] || 0) + 1;
      });

      // Calculer le temps moyen de résolution
      const avgResolutionTime = calculateAverageResolutionTime(requestsData);

      // Générer des données de tendance hebdomadaire (exemple simplifié)
      const today = new Date();
      const weeklyTrends = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toLocaleDateString('fr-FR', { weekday: 'short' });
        
        // Compter les demandes créées ce jour-là
        const count = requestsData.filter(req => {
          const reqDate = new Date(req.createdAt);
          return reqDate.getDate() === date.getDate() && 
                 reqDate.getMonth() === date.getMonth() &&
                 reqDate.getFullYear() === date.getFullYear();
        }).length;
        
        weeklyTrends.push({ day: dateStr, count });
      }

      setStats({
        byStatus: statusStats,
        byPriority: priorityStats,
        recentActivity: requestsData.slice(0, 5),
        averageResolutionTime: avgResolutionTime,
        weeklyTrends
      });

      // Charger les interventions planifiées pour ce service (Solution D)
      try {
        const intSnap = await getDocs(query(
          collection(db, COLLECTIONS.INTERVENTIONS),
          where('serviceId', '==', serviceId)
        ));
        const map = {};
        intSnap.docs.forEach(d => {
          const data = d.data();
          if (data.workRequestId) {
            map[data.workRequestId] = { id: d.id, ...data, date: data.date?.toDate() || null };
          }
        });
        setInterventionsMap(map);
      } catch (_) {}

      setRequests(requestsData);
      setFilteredRequests(requestsData);
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes:', error);
      setError('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  }, [currentUser, userService]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    // Filtrer les demandes en fonction des critères de recherche et de filtre
    let filtered = [...requests];
    
    if (searchTerm) {
      filtered = filtered.filter(req => 
        (req.title && req.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (req.description && req.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (req.requestingServiceName && req.requestingServiceName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (req.location && req.location.room && req.location.room.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(req => req.status === statusFilter);
    }
    
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(req => req.priority === priorityFilter);
    }
    
    setFilteredRequests(filtered);
  }, [searchTerm, statusFilter, priorityFilter, requests]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  function openPlanningDialog(request) {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    setPlanningForm({ date: dateStr, time: '09:00', duration: 60, technicianName: '', equipment: '' });
    setPlanningError('');
    setPlanningRequest(request);
  }

  async function handlePlanningSubmit() {
    if (!planningForm.date || !planningForm.time || !planningForm.technicianName.trim()) {
      setPlanningError('Date, heure et nom du technicien sont obligatoires.');
      return;
    }
    setPlanningLoading(true);
    setPlanningError('');
    try {
      const [hours, minutes] = planningForm.time.split(':').map(Number);
      const interventionDate = new Date(planningForm.date);
      interventionDate.setHours(hours, minutes, 0, 0);

      let serviceId = userService?.id;
      if (!serviceId && currentUser) {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, currentUser.uid));
        if (userDoc.exists()) serviceId = userDoc.data().serviceId;
      }

      await addDoc(collection(db, COLLECTIONS.INTERVENTIONS), {
        title: planningRequest.title || `Intervention — ${translateCategory(planningRequest.problemType)}`,
        description: planningRequest.description || '',
        date: interventionDate,
        duration: Number(planningForm.duration),
        location: extractLocation(planningRequest.location),
        workRequestId: planningRequest.id,
        equipment: planningForm.equipment || '',
        technicianName: planningForm.technicianName.trim(),
        status: 'planned',
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        serviceId: serviceId || currentUser.uid,
      });

      await updateDoc(doc(db, COLLECTIONS.WORK_REQUESTS, planningRequest.id), {
        status: 'in_progress',
        scheduledAt: interventionDate,
        updatedAt: serverTimestamp(),
      });

      setPlanningRequest(null);
      fetchRequests();
    } catch (err) {
      console.error('handlePlanningSubmit:', err);
      setPlanningError('Erreur lors de la planification : ' + err.message);
    } finally {
      setPlanningLoading(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Tableau de Bord Service Technique
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab icon={<AssignmentIcon />} label="Demandes" />
        <Tab icon={<EqualizerIcon />} label="Statistiques" />
        <Tab icon={<CalendarTodayIcon />} label="Planification" />
      </Tabs>

      {/* Onglet Demandes */}
      {tabValue === 0 && (
        <>
          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              label="Rechercher"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1, minWidth: '200px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: '150px' }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="pending">En attente</MenuItem>
                <MenuItem value="in_progress">En cours</MenuItem>
                <MenuItem value="completed">Terminé</MenuItem>
                <MenuItem value="cancelled">Annulé</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: '150px' }}>
              <InputLabel>Priorité</InputLabel>
              <Select
                value={priorityFilter}
                label="Priorité"
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <MenuItem value="all">Toutes</MenuItem>
                <MenuItem value="high">Haute</MenuItem>
                <MenuItem value="medium">Moyenne</MenuItem>
                <MenuItem value="low">Basse</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Demandes de travaux assignées ({filteredRequests.length})
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Service demandeur</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Localisation</TableCell>
                    <TableCell>Priorité</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRequests.length > 0 ? (
                    filteredRequests.map((request) => {
                      const plannedIntervention = interventionsMap[request.id];
                      return (
                      <TableRow key={request.id}>
                        <TableCell>{request.createdAt?.toLocaleDateString()}</TableCell>
                        <TableCell>{request.requestingServiceName}</TableCell>
                        <TableCell>{translateCategory(request.problemType)}</TableCell>
                        <TableCell>{extractLocation(request.location)}</TableCell>
                        <TableCell>
                          <Chip
                            label={PRIORITY_DISPLAY[request.priority] || request.priority || 'Non spécifié'}
                            size="small"
                            color={getPriorityColor(request.priority)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                            <Chip
                              label={STATUS_DISPLAY[request.status] || request.status || 'Non spécifié'}
                              size="small"
                              color={getStatusColor(request.status)}
                            />
                            {/* Solution D — badge intervention planifiée */}
                            {plannedIntervention?.date && (
                              <Chip
                                icon={<EventAvailableIcon />}
                                label={`Planifiée le ${format(plannedIntervention.date, 'dd/MM à HH:mm', { locale: fr })}`}
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setSelectedRequest(request)}
                            >
                              Gérer
                            </Button>
                            {/* Solution A — bouton Planifier */}
                            {!plannedIntervention && request.status !== 'completed' && request.status !== 'cancelled' && (
                              <Tooltip title="Planifier une intervention">
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="secondary"
                                  startIcon={<CalendarMonthIcon />}
                                  onClick={() => openPlanningDialog(request)}
                                >
                                  Planifier
                                </Button>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Aucune demande ne correspond aux critères de recherche
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Onglet Statistiques */}
      {tabValue === 1 && (
        <>
          <Grid container spacing={3}>
            {/* KPIs */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Demandes totales
                  </Typography>
                  <Typography variant="h4">
                    {requests.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    En attente
                  </Typography>
                  <Typography variant="h4">
                    {stats.byStatus['pending'] || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    En cours
                  </Typography>
                  <Typography variant="h4">
                    {stats.byStatus['in_progress'] || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Temps moyen de résolution
                  </Typography>
                  <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccessTimeIcon sx={{ mr: 1 }} />
                    {stats.averageResolutionTime} h
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Graphiques */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 300 }}>
                <Typography variant="h6" gutterBottom>
                  Demandes par statut
                </Typography>
                {Object.keys(stats.byStatus).length > 0 ? (
                  <PieChart
                    series={[
                      {
                        data: Object.entries(stats.byStatus).map(([name, value]) => ({
                          id: name,
                          value,
                          label: STATUS_DISPLAY[name] || name
                        })),
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                      },
                    ]}
                    height={250}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Aucune donnée disponible
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 300 }}>
                <Typography variant="h6" gutterBottom>
                  Demandes par priorité
                </Typography>
                {Object.keys(stats.byPriority).length > 0 ? (
                  <BarChart
                    dataset={Object.entries(stats.byPriority).map(([name, value]) => ({
                      priority: PRIORITY_DISPLAY[name] || name,
                      count: value
                    }))}
                    yAxis={[{ scaleType: 'linear' }]}
                    xAxis={[{ scaleType: 'band', dataKey: 'priority' }]}
                    series={[{ dataKey: 'count', label: 'Nombre de demandes' }]}
                    height={250}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Aucune donnée disponible
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  Tendance hebdomadaire
                </Typography>
                {stats.weeklyTrends.length > 0 ? (
                  <LineChart
                    xAxis={[{ 
                      data: stats.weeklyTrends.map(item => item.day),
                      scaleType: 'point' 
                    }]}
                    series={[
                      {
                        data: stats.weeklyTrends.map(item => item.count),
                        label: 'Nouvelles demandes',
                        area: true,
                      },
                    ]}
                    height={300}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Aucune donnée disponible
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Onglet Planification */}
      {tabValue === 2 && (
        <Paper sx={{ p: 2 }}>
          <InterventionCalendar />
        </Paper>
      )}

      {selectedRequest && (
        <WorkRequestHandler
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onUpdateSuccess={() => {
            setSelectedRequest(null);
            fetchRequests();
          }}
        />
      )}

      {/* ── Dialog planification (Solution A) ── */}
      <Dialog open={Boolean(planningRequest)} onClose={() => !planningLoading && setPlanningRequest(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonthIcon color="secondary" />
          Planifier l'intervention
        </DialogTitle>
        <DialogContent dividers>
          {/* Résumé de la demande */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">Demande concernée</Typography>
            <Typography variant="body2" fontWeight={600}>{planningRequest?.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {translateCategory(planningRequest?.problemType)} — {extractLocation(planningRequest?.location)}
            </Typography>
          </Paper>

          {planningError && <Alert severity="error" sx={{ mb: 2 }}>{planningError}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Date d'intervention" type="date"
                value={planningForm.date}
                onChange={e => setPlanningForm(p => ({ ...p, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                disabled={planningLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Heure" type="time"
                value={planningForm.time}
                onChange={e => setPlanningForm(p => ({ ...p, time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                disabled={planningLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Technicien responsable"
                placeholder="Ex : M. Martin"
                value={planningForm.technicianName}
                onChange={e => setPlanningForm(p => ({ ...p, technicianName: e.target.value }))}
                disabled={planningLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Durée estimée (minutes)" type="number"
                value={planningForm.duration}
                onChange={e => setPlanningForm(p => ({ ...p, duration: e.target.value }))}
                inputProps={{ min: 15, step: 15 }}
                disabled={planningLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Matériel / équipement nécessaire (optionnel)"
                placeholder="Ex : Clé à molette, joint de remplacement..."
                value={planningForm.equipment}
                onChange={e => setPlanningForm(p => ({ ...p, equipment: e.target.value }))}
                disabled={planningLoading}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setPlanningRequest(null)} disabled={planningLoading}>Annuler</Button>
          <Button variant="contained" color="secondary" onClick={handlePlanningSubmit} disabled={planningLoading}
            startIcon={planningLoading ? <CircularProgress size={16} /> : <EventAvailableIcon />}
          >
            {planningLoading ? 'Planification…' : 'Confirmer la planification'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TechnicalServiceDashboard;
